"use client"

import { useState, useContext } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  alpha,
  useTheme,
  Paper,
  Fade,
  Slide,
  Chip,
} from "@mui/material"
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material"
import axios from "axios"
import { AppContext } from "../contexts/AppContext"

const ChangePasswordModal = ({ open, onClose }) => {
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { state } = useContext(AppContext)
  const theme = useTheme()

  // Password strength indicators
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "None" }

    let strength = 0
    if (password.length >= 8) strength += 1
    if (/[A-Z]/.test(password)) strength += 1
    if (/[0-9]/.test(password)) strength += 1
    if (/[^A-Za-z0-9]/.test(password)) strength += 1

    const labels = ["Weak", "Fair", "Good", "Strong"]
    const colors = ["error", "warning", "info", "success"]

    return {
      strength,
      label: labels[strength - 1] || "Weak",
      color: colors[strength - 1] || "error",
    }
  }

  const passwordStrength = getPasswordStrength(newPassword)
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword

  const handleChangePassword = async () => {
    setLoading(true)
    setError(null)

    if (newPassword !== confirmPassword) {
      setError("New passwords don't match")
      setLoading(false)
      return
    }

    try {
      const response = await axios.post(
        "http://localhost/generic-app/changePassword.php",
        {
          oldPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${state.token}`, // Include bearer token in headers
          },
        },
      )

      if (response.data?.success) {
        onClose()
      } else {
        setError(response?.data?.error || "Change password failed")
      }
    } catch (error) {
      setError(error.response?.data?.error || "Change password failed")
      console.error("Error changing password:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        elevation: 5,
        sx: {
          borderRadius: 2,
          overflow: "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <SecurityIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update your account security credentials
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3 }}>
        <Fade in={!!error}>
          <Box sx={{ mb: error ? 3 : 0 }}>
            {error && (
              <Alert
                severity="error"
                variant="filled"
                sx={{
                  borderRadius: 1.5,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Fade>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              border: "1px solid",
              borderColor: alpha(theme.palette.warning.main, 0.2),
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
              <LockOpenIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
              Current Password
            </Typography>

            <TextField
              label="Enter Current Password"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          <Divider>
            <Chip icon={<SecurityIcon fontSize="small" />} label="New Credentials" size="small" variant="outlined" />
          </Divider>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <LockIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                New Password
              </Typography>

              <TextField
                label="Enter New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {newPassword && (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">
                    Password Strength:
                  </Typography>
                  <Chip size="small" label={passwordStrength.label} color={passwordStrength.color} variant="outlined" />
                </Box>
              )}

              {newPassword && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    Password Requirements:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Chip
                      size="small"
                      icon={
                        newPassword.length >= 8 ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />
                      }
                      label="8+ Characters"
                      color={newPassword.length >= 8 ? "success" : "default"}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                    <Chip
                      size="small"
                      icon={
                        /[A-Z]/.test(newPassword) ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <ErrorIcon fontSize="small" />
                        )
                      }
                      label="Uppercase"
                      color={/[A-Z]/.test(newPassword) ? "success" : "default"}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                    <Chip
                      size="small"
                      icon={
                        /[0-9]/.test(newPassword) ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <ErrorIcon fontSize="small" />
                        )
                      }
                      label="Number"
                      color={/[0-9]/.test(newPassword) ? "success" : "default"}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                    <Chip
                      size="small"
                      icon={
                        /[^A-Za-z0-9]/.test(newPassword) ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <ErrorIcon fontSize="small" />
                        )
                      }
                      label="Special Char"
                      color={/[^A-Za-z0-9]/.test(newPassword) ? "success" : "default"}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <LockIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                Confirm Password
              </Typography>

              <TextField
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                variant="outlined"
                error={confirmPassword && !passwordsMatch}
                helperText={confirmPassword && !passwordsMatch ? "Passwords don't match" : ""}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {confirmPassword && passwordsMatch && (
                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                  <Typography variant="caption" color="success.main">
                    Passwords match
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{ borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleChangePassword}
          variant="contained"
          color="primary"
          disabled={loading || !oldPassword || !newPassword || !confirmPassword || !passwordsMatch}
          startIcon={loading ? null : <SaveIcon />}
          sx={{
            borderRadius: 1.5,
            px: 3,
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              boxShadow: "0 6px 15px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Change Password"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ChangePasswordModal

